import React, { useState, useMemo } from "react";
import { Group, Rect, Text, Line } from "react-konva";

// Определяем тип для позиций костей
interface BonePosition {
  top: number;
  left: number;
}

// Тип для элемента внутри приёма пищи
type MealItem = {
  id: number;
  date: string;
};

type FishBoneProps = {
  weeks: MealItem[][][]; // Массив недель
  width: number;
  height: number;
  onBoneClick?: (data: {
    date: string;
    mealType: string;
    dayIndex: number;
    weekIndex: number;
  }) => void;
};

const FishBone = ({
  weeks = [],
  width,
  height,
  onBoneClick,
}: FishBoneProps) => {
  const [hoveredBone, setHoveredBone] = useState<string | null>(null);

  // Стили для одной недели
  const styles = {
    fishContainer: {
      width: 600,
      height: 650,
    },
    upContainer: {
      paddingLeft: 150,
      gap: 180,
      width: 200,
      height: 200,
    },
    downContainer: {
      paddingLeft: 110,
      gap: 280,
      width: 200,
      height: 200,
    },
    fishBoneContainer: {
      width: 200,
      height: 200,
    },
    bigFishBone: {
      stroke: "black",
      strokeWidth: 2,
    },
    smallFishBone: {
      width: 40,
      height: 2,
      fill: "black",
    },
    middleLine: {
      height: 2,
      fill: "black",
    },
    dateText: {
      fontSize: 8,
      fill: "#000",
      fontFamily: "Arial, sans-serif",
      opacity: 1,
    },
    // dateTextHovered: {
    //   opacity: 1,
    // },
  };

  // Вычисляем общую ширину и масштаб
  const { totalWidth, scale } = useMemo(() => {
    const weekWidth = styles.fishContainer.width;
    const gap = 100; // Расстояние между неделями
    const totalWidth = weeks.length * weekWidth + (weeks.length - 1) * gap;

    // Масштаб для вписывания в доступную ширину
    const scale = Math.min(1, width / totalWidth);

    return { totalWidth, scale };
  }, [weeks.length, width, styles.fishContainer.width]);

  function calculateDynamicFishBonePositions(count?: number): BonePosition[] {
    const finalCount = count ?? 7;
    if (finalCount <= 0) return [];
    if (finalCount === 1) return [{ top: 100, left: 2 }];

    const positions2 = [
      { top: 50, left: -25 },
      { top: 140, left: -15 },
    ];
    const positions3 = [
      { top: 40, left: -35 },
      { top: 100, left: -38 },
      { top: 160, left: -2 },
    ];
    const positions4 = [
      { top: 40, left: -35 },
      { top: 70, left: -56 },
      { top: 110, left: 7 },
      { top: 160, left: -2 },
    ];
    const positions5 = [
      { top: 30, left: -35 },
      { top: 60, left: -60 },
      { top: 90, left: -5 },
      { top: 130, left: -20 },
      { top: 160, left: 38 },
    ];
    const positions6 = [
      { top: 30, left: -38 },
      { top: 55, left: -65 },
      { top: 80, left: -10 },
      { top: 110, left: -30 },
      { top: 145, left: 30 },
      { top: 180, left: 10 },
    ];
    const positions7 = [
      { top: 30, left: -38 },
      { top: 60, left: -60 },
      { top: 80, left: -10 },
      { top: 110, left: -33 },
      { top: 140, left: 25 },
      { top: 160, left: -2 },
      { top: 180, left: 50 },
    ];

    if (finalCount === 2) return positions2;
    if (finalCount === 3) return positions3;
    if (finalCount === 4) return positions4;
    if (finalCount === 5) return positions5;
    if (finalCount === 6) return positions6;
    if (finalCount === 7) return positions7;

    return [];
  }

  function calculateDynamicFishBonePositionsDown(
    count?: number
  ): BonePosition[] {
    const finalCount = count ?? 7;
    if (finalCount <= 0) return [];
    if (finalCount === 1) return [{ top: 100, left: 2 }];

    const positions2 = [
      { top: 50, left: -10 },
      { top: 140, left: -20 },
    ];
    const positions3 = [
      { top: 30, left: 42 },
      { top: 90, left: -33 },
      { top: 160, left: -33 },
    ];
    const positions4 = [
      { top: 30, left: 40 },
      { top: 70, left: -22 },
      { top: 110, left: -5 },
      { top: 160, left: -72 },
    ];
    const positions5 = [
      { top: 30, left: 41 },
      { top: 60, left: -17 },
      { top: 100, left: 3 },
      { top: 130, left: -55 },
      { top: 160, left: -32 },
    ];
    const positions6 = [
      { top: 30, left: 42 },
      { top: 55, left: -10 },
      { top: 80, left: 12 },
      { top: 110, left: -42 },
      { top: 145, left: -25 },
      { top: 170, left: -80 },
    ];
    const positions7 = [
      { top: 30, left: 42 },
      { top: 50, left: -10 },
      { top: 80, left: 13 },
      { top: 110, left: -43 },
      { top: 140, left: -20 },
      { top: 160, left: -73 },
      { top: 180, left: -45 },
    ];

    if (finalCount === 2) return positions2;
    if (finalCount === 3) return positions3;
    if (finalCount === 4) return positions4;
    if (finalCount === 5) return positions5;
    if (finalCount === 6) return positions6;
    if (finalCount === 7) return positions7;

    return [];
  }

  // Большая кость (верхняя) - наклон на 30 градусов
  const renderBigFishBone = (x: number, y: number) => {
    const lineLength = 200;
    const angle = (30 * Math.PI) / 180;
    const endX = lineLength * Math.sin(angle);
    const endY = lineLength * Math.cos(angle);

    return (
      <Group x={x} y={y}>
        <Line
          points={[0, 0, endX, endY]}
          stroke={styles.bigFishBone.stroke}
          strokeWidth={styles.bigFishBone.strokeWidth}
        />
      </Group>
    );
  };

  // Большая кость (нижняя) - наклон на -30 градусов
  const renderBigFishBoneDown = (x: number, y: number) => {
    const lineLength = 230;
    const angle = (-30 * Math.PI) / 180;
    const endX = lineLength * Math.sin(angle);
    const endY = lineLength * Math.cos(angle);

    return (
      <Group x={x} y={y}>
        <Line
          points={[0, 0, endX, endY]}
          stroke={styles.bigFishBone.stroke}
          strokeWidth={styles.bigFishBone.strokeWidth}
        />
      </Group>
    );
  };

  // Маленькая кость с датой
  const renderSmallFishBone = (
    position: BonePosition,
    text: string,
    index: number,
    boneType: "up" | "down",
    containerType: "breakfast" | "dinner" | "supper" | "lunch",
    weekIndex: number
  ) => {
    const boneId = `${weekIndex}-${containerType}-${boneType}-${index}`;
    const isHovered = hoveredBone === boneId;

    const handleClick = (e: any) => {
      e.cancelBubble = true;
      console.log("Кость кликнута:", {
        boneId,
        text,
        containerType,
        index,
        weekIndex,
      });
      if (onBoneClick) {
        onBoneClick({
          date: text,
          mealType: containerType,
          dayIndex: index,
          weekIndex: weekIndex,
        });
      }
    };

    return (
      <Group
        key={boneId}
        x={position.left}
        y={position.top}
        onMouseEnter={() => setHoveredBone(boneId)}
        onMouseLeave={() => setHoveredBone(null)}
        onClick={handleClick}
        onTap={handleClick}
      >
        {/* Малая кость */}
        <Rect
          width={styles.smallFishBone.width}
          height={styles.smallFishBone.height}
          fill={isHovered ? "#666" : styles.smallFishBone.fill}
          cornerRadius={1}
          listening={true}
        />

        {/* Дата (показывается при наведении) */}
        <Text
          text={text}
          x={-15}
          y={-20}
          width={70}
          height={20}
          align="center"
          verticalAlign="middle"
          fontSize={styles.dateText.fontSize}
          fill={styles.dateText.fill}
          fontFamily={styles.dateText.fontFamily}
          // opacity={
          //   isHovered ? styles.dateTextHovered.opacity : styles.dateText.opacity
          // }
          //   listening={false}
        />
      </Group>
    );
  };

  // Контейнер для костей (верхний) для одной недели
  const renderUpFishBoneContainer = (
    positions: BonePosition[],
    weekItems: MealItem[],
    containerX: number,
    containerType: "breakfast" | "dinner",
    weekIndex: number
  ) => (
    <Group
      x={containerX}
      y={0}
      width={styles.fishBoneContainer.width}
      height={styles.fishBoneContainer.height}
    >
      {renderBigFishBone(-38, 30)}
      {positions.map((position, index) =>
        renderSmallFishBone(
          position,
          weekItems?.[index]?.date || `День ${index + 1}`,
          index,
          "up",
          containerType,
          weekIndex
        )
      )}
    </Group>
  );

  // Контейнер для костей (нижний) для одной недели
  const renderDownFishBoneContainer = (
    positions: BonePosition[],
    weekItems: MealItem[],
    containerX: number,
    containerType: "supper" | "lunch",
    weekIndex: number
  ) => (
    <Group
      x={containerX}
      y={0}
      width={styles.fishBoneContainer.width}
      height={styles.fishBoneContainer.height}
    >
      {renderBigFishBoneDown(58, 0)}
      {positions.map((position, index) =>
        renderSmallFishBone(
          position,
          weekItems?.[index]?.date || `День ${index + 1}`,
          index,
          "down",
          containerType,
          weekIndex
        )
      )}
    </Group>
  );

  // Одна неделя
  const renderWeek = (week: MealItem[][], weekIndex: number, x: number) => {
    const positionsBreakfast = calculateDynamicFishBonePositions(
      week[0]?.length || 0
    );
    const positionsDinner = calculateDynamicFishBonePositions(
      week[1]?.length || 0
    );
    const positionsDownSupper = calculateDynamicFishBonePositionsDown(
      week[2]?.length || 0
    );
    const positionsDownLunch = calculateDynamicFishBonePositionsDown(
      week[3]?.length || 0
    );

    return (
      <Group key={weekIndex} x={x} y={0}>
        {/* Верхняя часть - завтрак и ужин */}
        <Group x={styles.upContainer.paddingLeft} y={0}>
          <Group x={0} y={0}>
            {renderUpFishBoneContainer(
              positionsBreakfast,
              week[0] || [],
              0,
              "breakfast",
              weekIndex
            )}
          </Group>
          <Group x={styles.upContainer.gap} y={0}>
            {renderUpFishBoneContainer(
              positionsDinner,
              week[1] || [],
              0,
              "dinner",
              weekIndex
            )}
          </Group>
        </Group>

        {/* Средняя линия для недели */}
        <Rect
          x={50}
          y={styles.upContainer.height}
          width={styles.fishContainer.width - 100}
          height={styles.middleLine.height}
          fill={styles.middleLine.fill}
        />

        {/* Нижняя часть - ужин и ланч */}
        <Group
          x={styles.downContainer.paddingLeft}
          y={styles.upContainer.height + styles.middleLine.height}
        >
          <Group x={0} y={0}>
            {renderDownFishBoneContainer(
              positionsDownSupper,
              week[2] || [],
              0,
              "supper",
              weekIndex
            )}
          </Group>
          <Group x={styles.downContainer.gap} y={0}>
            {renderDownFishBoneContainer(
              positionsDownLunch,
              week[3] || [],
              0,
              "lunch",
              weekIndex
            )}
          </Group>
        </Group>

        {/* Подпись недели */}
        <Text
          text={`Неделя ${weekIndex + 1}`}
          x={styles.fishContainer.width / 4 - 30}
          y={styles.fishContainer.height - 150}
          fontSize={12}
          fill="#333"
        />
      </Group>
    );
  };

  return (
    <Group
      scaleX={scale}
      scaleY={scale}
      x={(width - totalWidth * scale) / 2} // Центрирование
      y={(height - styles.fishContainer.height * scale) / 2}
    >
      {/* Главная средняя линия через все недели */}
      <Line
        points={[
          0,
          styles.upContainer.height,
          totalWidth,
          styles.upContainer.height,
        ]}
        stroke="black"
        strokeWidth={4}
      />

      {/* Рендерим все недели */}
      {weeks.map((week, index) => {
        const weekX = index * (styles.fishContainer.width + 100); // 100 - отступ между неделями
        return renderWeek(week, index, weekX);
      })}
    </Group>
  );
};

export default FishBone;
